// Translations + helpers (`tFn`, `isRTL`, `pickLang`, `pickFromMap`).
//
// Pure functions only — no DOM.
//
// Type strategy: English is the source of truth. `TranslationKey` is
// `keyof typeof en`, and the other 3 dictionaries are typed
// `Translations`, so TS errors on missing keys when a new English string
// is added but its translations are forgotten. `tFn` itself accepts any
// string for now — migration code paths still pass keys that aren't yet
// in the dict (with inline fallbacks).
//
// ─── F-08: one dictionary per language, loaded on demand ────────────
//
// The four dictionaries used to be four object literals in this file,
// which meant every user downloaded ~99 kB of strings to read ~25 kB of
// them. They now live in ./dict/<lang>.ts and arrive through `import()`.
//
// The constraint that shaped the design: **`t()` stays synchronous.**
// It is called in the body of ~700 components; making it async, or
// making it return a promise, would have been a rewrite of the app
// rather than a change to the bundle. So the asynchrony is pushed to
// the one place that can absorb it — boot order:
//
//   1. `loadLang(lang)` is awaited in main.tsx BEFORE the first render.
//   2. A language change awaits the new dictionary before it takes
//      effect (see `useLangSwitch` in apps/web).
//
// That is what keeps a reader from ever seeing raw keys. `tFn` also
// falls back to any dictionary that IS loaded rather than to the key
// itself, so a bug in the ordering degrades to the wrong language for
// one frame instead of to `chQuotaPerDay` in the middle of a sentence.

export type { Lang, TranslationKey, Translations } from './types';

import type { Lang, Translations } from './types';

export { initialsOf } from './initials';

// ─── Loading ─────────────────────────────────────────────────────────

/** One dynamic import per language. Written as a literal map rather than
 * `import(\`./dict/${lang}\`)` so the bundler can see all four targets
 * and emit one chunk each; a template literal would make it guess. */
const LOADERS: Record<Lang, () => Promise<{ default: Translations }>> = {
  en: () => import('./dict/en'),
  he: () => import('./dict/he'),
  hy: () => import('./dict/hy'),
  ru: () => import('./dict/ru'),
};

const loaded = new Map<Lang, Translations>();
/** Whatever arrived first. Used only when `tFn` is asked for a language
 * nobody loaded — a boot-order bug, which should read as the wrong
 * language rather than as raw keys. */
let firstLoaded: Translations | null = null;
/** In-flight loads, so two callers asking at once make one request. */
const inFlight = new Map<Lang, Promise<void>>();

/**
 * Fetch one dictionary. Idempotent, and safe to call concurrently.
 *
 * Awaited before the first render and before a language change takes
 * effect. A rejection propagates: the caller decides whether to keep the
 * current language or show something — swallowing it here would leave
 * the app rendering a language it does not have.
 */
export function loadLang(lang: Lang): Promise<void> {
  if (loaded.has(lang)) return Promise.resolve();
  const pending = inFlight.get(lang);
  if (pending) return pending;
  const p = LOADERS[lang]()
    .then((mod) => {
      loaded.set(lang, mod.default);
      firstLoaded ??= mod.default;
    })
    .finally(() => inFlight.delete(lang));
  inFlight.set(lang, p);
  return p;
}

/** Whether `t()` for this language will return real strings. */
export function isLangLoaded(lang: Lang): boolean {
  return loaded.has(lang);
}

/** Test seam: drop everything so a test can assert the unloaded path. */
export function __resetLangsForTest(): void {
  loaded.clear();
  inFlight.clear();
  firstLoaded = null;
}

// ─── Helpers ─────────────────────────────────────────────────────────

export const isRTL = (lang: Lang): boolean => lang === 'he';

/**
 * Builds a `t(key)` lookup for the given language.
 *
 * There is no per-key fallback to English any more, and there never
 * needed to be: a test asserts the four dictionaries hold identical key
 * sets, so a key present in English is present everywhere. What remains
 * is the fallback for a key that is in NO dictionary — migration code
 * still passes raw strings — which renders as the key itself.
 *
 * The whole-dictionary fallback (`firstLoaded`) is different and exists
 * for a different reason: see the boot-order note at the top of the file.
 *
 * Accepts `string` (not `TranslationKey`) so migration code can pass keys
 * that haven't been added to the dict yet without TS errors. Tighten when
 * the migration completes.
 */
const T_CACHE = new Map<Lang, (key: string) => string>();

export function tFn(lang: Lang): (key: string) => string {
  // One function per language, kept forever (there are four).
  //
  // The identity matters as much as the behaviour. Callers write
  // `const t = tFn(lang)` at the top of a component and then put `t` in
  // a useCallback/useEffect dependency array. A fresh closure per call
  // makes those deps change on every render, and a `useEffect(load)`
  // whose `load` depends on `t` becomes an infinite fetch loop —
  // exactly what the Users tab's detail panel did until F-05: it
  // re-requested the user forever and never left its loading state.
  //
  // The lookup depends on nothing but `lang`, so caching is not an
  // optimisation, it is the honest signature.
  let fn = T_CACHE.get(lang);
  if (!fn) {
    fn = (key: string) => {
      // Read at call time, not at capture time: the dictionary may land
      // after this function was handed out, and the identity has to stay
      // stable regardless (components put `t` in dependency arrays).
      const dict = (loaded.get(lang) ?? firstLoaded) as Record<string, string> | null;
      return dict?.[key] ?? key;
    };
    T_CACHE.set(lang, fn);
  }
  return fn;
}

const LANG_SUFFIX: Record<Lang, string> = { en: 'En', hy: 'Hy', ru: 'Ru', he: 'He' };

/**
 * pickLang — resolves a content field to the right language variant.
 *
 * Intelligence content (events, digests, alerts) uses a flat suffix
 * pattern. F-06: **English is now a suffix too**. nayto-core writes
 * `headlineEn`, `summaryEn`, `whyItMattersEn`, and B-11 adds `…He` and
 * `…Hy` beside them. The old shape had a bare `headline` as the English
 * base, which is why the Live screen showed "(no headline)" on real
 * events — it was reading a field the backend had stopped writing.
 *
 * Resolution order, per call:
 *
 *   1. `<base><Lang>`  — the reader's own language
 *   2. `<base>En`      — English, which nayto-core always writes
 *   3. `<base>`        — the bare field, for anything still on the old
 *                        shape. Nothing produces it today; it costs one
 *                        lookup and it is the difference between an old
 *                        cached document rendering and rendering blank.
 *
 *   pickLang(ev, 'he', 'headline')      // headlineHe → headlineEn → headline
 *   pickLang(ev, 'hy', 'whyItMatters')  // whyItMattersHy → …En → whyItMatters
 *
 * There is no Russian in this contract; `ru` asks for `…Ru`, misses,
 * and lands on English like any other unwritten language.
 *
 * An empty string counts as absent. B-11 fills He and Hy progressively,
 * and a row that exists but has not been translated yet must show the
 * English rather than a blank line where a headline should be.
 */
export function pickLang(
  obj: Record<string, unknown> | null | undefined,
  lang: Lang,
  base: string,
): string {
  if (!obj) return '';
  const read = (key: string): string | undefined => {
    const v = obj[key];
    return typeof v === 'string' && v !== '' ? v : undefined;
  };
  return read(base + LANG_SUFFIX[lang]) ?? read(base + 'En') ?? read(base) ?? '';
}

/** Resolves a `{ en, hy, ru, he }` map (e.g. digest.summaryByLang). */
export function pickFromMap(
  map: Partial<Record<Lang, string>> | null | undefined,
  lang: Lang,
): string {
  if (!map) return '';
  return map[lang] ?? map.en ?? '';
}

// ─── Compact number formatting ──────────────────────────────────────
//
// Shared helper consolidated from 4 in-app implementations
// (Home / Competitors / Trends / Listening) plus the upcoming
// Leaderboard + Compliance + Admin migrations. Default behavior matches
// the most-common variant — null/undefined → '0' — so existing call
// sites stay byte-identical when they switch to the import. Stats
// dashboards that prefer "no data" semantics can opt into '—' via
// `nullDisplay`.

export interface FormatKOptions {
  /** What to render for null/undefined/NaN. Defaults to '0' (Home /
   * Competitors / Listening behavior). Pass '—' for stats displays
   * where the absence of data is meaningful (Trends / Leaderboard). */
  nullDisplay?: string;
}

/**
 * Render a number in compact human-readable form: 14,210 → '14.2K',
 * 1,500,000 → '1.5M', 999 → '999'. Negatives are mirrored. One-decimal
 * precision; trailing '.0' stripped (so '1K' not '1.0K').
 */
export function formatK(n: number | null | undefined, opts?: FormatKOptions): string {
  if (n == null || Number.isNaN(Number(n))) return opts?.nullDisplay ?? '0';
  const v = Number(n);
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `${(v / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (abs >= 1_000) return `${(v / 1_000).toFixed(1).replace(/\.0$/, '')}K`;
  return String(Math.round(v));
}
